/** @file Inserts a socket's generated bones: the locked socket bone and the optional `<name>-grip2` bone. */
import type { Draft } from 'immer';
import type { Doc, Id, SocketDoc } from '@pose2d/format/types';
import type { CommandContext } from '../commandContext';
import { assertFreeName } from '../_shared/assertFreeName';
import { gripSetup } from './gripSetup';

/**
 * One past the last sibling's index under `parent` (0 for a first child), as bone.create does. Repeats that rule:
 * T-220 switches this to commands-structure K.1's `bone/nextSiblingIndex(doc, parent)` once both areas merge.
 */
function nextSibling(draft: Draft<Doc>, ctx: CommandContext, parent: Id): number {
  const last = ctx.index.children.get(parent)?.at(-1);
  return last === undefined ? 0 : (draft.bones[last]?.siblingIndex ?? -1) + 1;
}

/** Adds the bones `socket` names (NAME_TAKEN at `name` when a bone already uses the socket or grip-2 name). */
export function insertSocketBones(draft: Draft<Doc>, ctx: CommandContext, socket: SocketDoc): void {
  assertFreeName(ctx, 'bone', socket.name);
  const { setup, length } = gripSetup(socket.a, socket.b);
  const siblingIndex = nextSibling(draft, ctx, socket.parentBone);
  draft.bones[socket.bone] = { name: socket.name, parent: socket.parentBone, siblingIndex, length, inherit: 'normal', setup, kind: 'socket', ui: { locked: true } };
  if (socket.grip2Bone === undefined) return;
  const name = `${socket.name}-grip2`;
  assertFreeName(ctx, 'bone', name);
  const rest = { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1, shearX: 0, shearY: 0 };
  draft.bones[socket.grip2Bone] = { name, parent: socket.bone, siblingIndex: 0, length: 0, inherit: 'normal', setup: rest, kind: 'socketGrip2', ui: { locked: true } };
}
